import React, { FC, useCallback, useLayoutEffect, useState } from 'react';
import BlurExplainerBanner from 'ts/common/components/gallery/BlurExplainerBanner';
import { MOBILE_BLUR_SELECTOR } from 'ts/common/components/gallery/screenshot-deterrence/MobileBlur';

/** One key for every gallery: once dismissed, the banner stays hidden until the tab closes */
export const BLUR_BANNER_DISMISSED_KEY = 'sp.blurExplainerBannerDismissed';

const readDismissed = () => {
    try {
        return window.sessionStorage.getItem(BLUR_BANNER_DISMISSED_KEY) === '1';
    } catch {
        return false;
    }
};

/** Bars pinned to the bottom of the screen: the grid's bottom nav and the lightbox's photo toolbar */
const BOTTOM_BAR_SELECTOR = '[data-testid="bottom-nav"], photo-toolbar';

/** How far the tallest showing bottom bar reaches up from the viewport bottom; 0 when none is showing */
const measureBottomBarOffset = () =>
    Array.from(document.querySelectorAll(BOTTOM_BAR_SELECTOR)).reduce((offset, bar) => {
        const { top, height } = bar.getBoundingClientRect();

        return height > 0 ? Math.max(offset, window.innerHeight - top) : offset;
    }, 0);

interface IMobileBlurBannerProps {
    translateFilter: SpAngularJs.ITranslateFilter;
    /** The studio's custom title; falls back to the default translation */
    customTitle?: Nullable<string>;
    /** The studio's custom body; falls back to the default translation for the blur style */
    customBody?: Nullable<string>;
    blurType?: Nullable<SpApi.ScreenshotDeterrenceMobileStyle>;
}

/** Client gallery banner explaining the mobile screenshot-protection blur */
const MobileBlurBanner: FC<IMobileBlurBannerProps> = ({
    translateFilter,
    customTitle,
    customBody,
    blurType
}) => {
    const [isDismissed, setIsDismissed] = useState(readDismissed);
    const [isBlurring, setIsBlurring] = useState(false);
    const [bottomOffset, setBottomOffset] = useState(0);
    const isVisible = isBlurring && !isDismissed;

    // The blurred photos live in other React roots, so watch the page for them. Moving between
    // the grid and the lightbox also swaps the bottom bars without resizing the window.
    useLayoutEffect(() => {
        if (isDismissed) {
            return;
        }

        const update = () => {
            setIsBlurring(document.querySelector(MOBILE_BLUR_SELECTOR) !== null);
            setBottomOffset(measureBottomBarOffset());
        };

        let frame = 0;

        const scheduleUpdate = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(update);
        };

        const observer = new MutationObserver(scheduleUpdate);

        update();
        observer.observe(document.body, { childList: true, subtree: true });
        window.addEventListener('resize', scheduleUpdate);

        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            window.removeEventListener('resize', scheduleUpdate);
        };
    }, [isDismissed]);

    const handleClose = useCallback(() => {
        setIsDismissed(true);

        try {
            window.sessionStorage.setItem(BLUR_BANNER_DISMISSED_KEY, '1');
        } catch {
            // Storage unavailable (e.g. private mode); the banner stays hidden for this page view
        }
    }, []);

    if (!isVisible) {
        return null;
    }

    return (
        <BlurExplainerBanner
            title={customTitle || translateFilter('blurBannerTitle')}
            body={
                customBody ||
                translateFilter(
                    blurType === 'magic_reveal' ? 'blurBannerBodyMagicReveal' : 'blurBannerBody'
                )
            }
            closeLabel={translateFilter('close')}
            onClose={handleClose}
            bottomOffset={bottomOffset}
        />
    );
};

export default MobileBlurBanner;
